import { z } from "zod";

export const PASSWORD_MIN_LENGTH = 6;
export const PASSWORD_SPECIAL_REGEX = /[^A-Za-z0-9]/;
export const PASSWORD_UPPERCASE_REGEX = /[A-Z]/;

export const PASSWORD_HELP_TEXT =
  "At least 6 characters, including 1 capital letter and 1 special character";

export interface PasswordRequirement {
  id: "length" | "uppercase" | "special";
  label: string;
  /** Message shown when this requirement is the reason validation failed. */
  error: string;
  test: (password: string) => boolean;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: "length",
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters long.`,
    test: (p) => p.length >= PASSWORD_MIN_LENGTH,
  },
  {
    id: "uppercase",
    label: "1 capital letter (A-Z)",
    error: "Password must include at least 1 capital letter (A-Z).",
    test: (p) => PASSWORD_UPPERCASE_REGEX.test(p),
  },
  {
    id: "special",
    label: "1 special character (! @ # $ %…)",
    error: "Password must include at least 1 special character (for example ! @ # $ %).",
    test: (p) => PASSWORD_SPECIAL_REGEX.test(p),
  },
];

/** Returns an error message when the password fails the policy, otherwise null. */
export function validatePassword(password: string): string | null {
  const pwd = password ?? "";
  const failed = PASSWORD_REQUIREMENTS.filter((r) => !r.test(pwd));
  if (failed.length === 0) return null;
  if (failed.length === 1) return failed[0].error;
  return `Password is missing: ${failed.map((r) => r.label.toLowerCase()).join(", ")}.`;
}

export interface PasswordStrength {
  /** 0-100 */
  score: number;
  label: "Too weak" | "Weak" | "Good" | "Strong";
  meetsPolicy: boolean;
}

export function getPasswordStrength(password: string): PasswordStrength {
  const pwd = password ?? "";
  const met = PASSWORD_REQUIREMENTS.filter((r) => r.test(pwd)).length;
  const meetsPolicy = met === PASSWORD_REQUIREMENTS.length;

  // Base score from the policy requirements (max 75), plus bonus for longer passwords.
  let score = Math.round((met / PASSWORD_REQUIREMENTS.length) * 75);
  if (meetsPolicy) {
    if (pwd.length >= 10) score += 15;
    if (/[0-9]/.test(pwd)) score += 10;
  }
  score = Math.min(score, 100);

  let label: PasswordStrength["label"] = "Too weak";
  if (meetsPolicy) label = score >= 95 ? "Strong" : score >= 85 ? "Good" : "Weak";

  return { score, label, meetsPolicy };
}

export const passwordSchema = z
  .string()
  .max(100, "Password must be less than 100 characters")
  .refine((p) => validatePassword(p) === null, (p) => ({
    message: validatePassword(p) ?? "Invalid password",
  }));
